<template>
  <app-page :title="$t('employees.title')" :subtitle="$t('employees.subtitle')" icon="users">
    <template v-slot:infobox>
      <sme-info-box :title="$t('employees.infoBox.about.title')" id="about-employees" save-state>
        <i18n path="employees.infoBox.about.body1" tag="p">
          <template #enrolled
            ><strong>{{ $t('employees.infoBox.about.enrolledEmphasis') }}</strong></template
          >
        </i18n>
        <p>{{ $t('employees.infoBox.about.body2') }}</p>
      </sme-info-box>

      <sme-info-box :title="$t('employees.infoBox.inviting.title')" id="inviting-employees" save-state>
        <i18n path="employees.infoBox.inviting.body1" tag="p">
          <template #integrationsLink>
            <router-link :to="{ name: 'sme-integrations' }">{{
              $t('employees.infoBox.inviting.integrationsLinkText')
            }}</router-link>
          </template>
          <template #rotaLabel>{{ getBankingInfo.rota.label }}</template>
        </i18n>
        <i18n v-if="canAddEmployees" path="employees.infoBox.inviting.body2" tag="p">
          <template #uploadLink>
            <router-link :to="{ name: 'employees-add' }">{{
              $t('employees.infoBox.inviting.uploadLinkText')
            }}</router-link>
          </template>
        </i18n>
      </sme-info-box>

      <sme-info-box :title="$t('employees.infoBox.disabling.title')" id="disabling-employees" is-grouped save-state>
        <i18n path="employees.infoBox.disabling.body1" tag="p">
          <template #optOut
            ><strong>'{{ $t('employees.table.optOutButton') }}'</strong></template
          >
        </i18n>
        <i18n path="employees.infoBox.disabling.body2" tag="p">
          <template #complete
            ><strong>'{{ $t('employees.table.completeButton') }}'</strong></template
          >
        </i18n>
        <i18n path="employees.infoBox.disabling.body3" tag="p">
          <template #showLeavers
            ><strong>'{{ $t('employees.showLeaversLabel') }}'</strong></template
          >
        </i18n>
        <i18n path="employees.infoBox.disabling.body4" tag="p">
          <template #pauseFlexiblePay
            ><strong>'{{ $t('employees.pauseFlexiblePayLabel') }}'</strong></template
          >
        </i18n>
      </sme-info-box>

      <sme-info-box
        :title="$t('employees.infoBox.disablingVisibility.title')"
        id="disabling-employees-visibility"
        is-grouped
        save-state
      >
        <p>{{ $t('employees.infoBox.disablingVisibility.body1') }}</p>
      </sme-info-box>
    </template>

    <template v-slot:header v-if="state.claims.a">
      <b-button
        v-if="canAddEmployees && canAddSingleEmployee"
        @click="goToAddEmployee"
        variant="outline-primary"
        :disabled="!state?.paySchedule"
      >
        <font-awesome-icon :icon="['fad', 'user-plus']" class="mr-2" />{{ $t('employees.addEmployeeButton') }}
      </b-button>

      <span id="bulk-upload-employees-button">
        <b-button
          v-if="canAddEmployees"
          @click="goToBulkUpload"
          variant="outline-primary"
          :disabled="!state?.paySchedule"
        >
          <font-awesome-icon :icon="['fal', 'upload']" class="mr-2" /> {{ $t('employees.bulkImportButton') }}
        </b-button>
      </span>
      <b-tooltip v-if="!state?.paySchedule" target="bulk-upload-employees-button">{{
        $t('employees.bulkImportTooltip')
      }}</b-tooltip>
      <b-button
        v-if="isEnableShowBankingFile && state?.claims?.a"
        @click="downloadBankingReport"
        variant="outline-primary"
      >
        <font-awesome-icon :icon="['fad', 'download']" class="mr-2" />{{ $t('employees.downloadBankingReportButton') }}
      </b-button>
      <b-button
        v-if="canIntegrate && !integrationsState.state.integrations"
        :to="{ name: 'sme-integrations' }"
        variant="outline-primary"
      >
        <font-awesome-icon :icon="['fad', 'chart-network']" class="mr-2" />{{ $t('employees.setupIntegrationButton') }}
      </b-button>
    </template>

    <template v-slot:before-content>
      <b-col sm="12" class="mb-4">
        <page-metrics
          :metrics="employeeMetrics"
          :active-filter="activeMetricFilter"
          @filter-change="handleMetricFilterChange"
        />
      </b-col>
      <b-col sm="12" class="mb-4" v-if="employeesToReinviteCount > 0">
        <b-card>
          <h4 class="font-weight-bold reinvite-title">
            {{
              employeesToReinviteCount === 1
                ? $t('employees.reinviteTitleSingular')
                : $t('employees.reinviteTitlePlural', { count: employeesToReinviteCount })
            }}
          </h4>
          <p>
            {{ $t('employees.reinviteBodyIntro') }}
            {{
              employeesToReinviteCount === 1
                ? $t('employees.reinviteBodySingular')
                : $t('employees.reinviteBodyPlural', { count: employeesToReinviteCount })
            }}
          </p>
          <b-button variant="primary" @click="goToReinvitePage" class="d-block mt-3">
            {{ $t('employees.reinviteButton') }}
          </b-button>
        </b-card>
      </b-col>
    </template>

    <template v-slot:custom-content>
      <b-col>
        <employees-list ref="employeesList" @metric-filter-changed="handleMetricFilterChanged" />
      </b-col>
    </template>
  </app-page>
</template>

<script>
import ApiClient from '@/ApiClient';
import { getBankingInfo } from '@/Banking';
import AppPage from '@/components/AppPage.vue';
import PageMetrics from '@/components/PageMetrics.vue';
import SmeInfoBox from '@/components/atoms/SmeInfoBox.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import EmployeesList from '@/pages/employees/components/EmployeesList.vue';
import IntegrationsState from '@/state/IntegrationsState';
import State from '@/state/State';
import useEmployees from '@/state/composables/useEmployees';
import useAnalytics from '@/state/composables/useAnalytics';

export default {
  name: 'Employees',
  components: { AppPage, EmployeesList, PageMetrics, SmeInfoBox },
  data() {
    return {
      state: State.state,
      integrationsState: IntegrationsState,
      employeesToReinviteCount: 0,
      activeMetricFilter: null,
    };
  },
  computed: {
    getBankingInfo: () => getBankingInfo(),
    employeeMetrics() {
      return [
        {
          value: this.employeeStateCounts.ELIGIBLE,
          title: this.$t('employees.metrics.eligible'),
          filterKey: 'ELIGIBLE',
        },
        {
          value: this.employeeStateCounts.ENROLLED,
          title: this.$t('employees.metrics.enrolled'),
          filterKey: 'ENROLLED',
        },
        {
          value: this.employeeStateCounts.ENROLLING,
          title: this.$t('employees.metrics.enrolling'),
          filterKey: 'ENROLLING',
        },
      ];
    },
  },
  setup() {
    const { employeeStateCounts, employeeStateCountsLoading, getEnrolmentSnapshot } = useEmployees();
    const { postAnalyticsEvent } = useAnalytics();

    getEnrolmentSnapshot();

    const {
      canAddEmployees,
      canAddSingleEmployee,
      isEnableEmployeeForm,
      isEnableShowBankingFile,
      canIntegrate,
      isUkCompany,
      isDisableSelfServiceReinviteEmail,
    } = useFeatureFlags();

    return {
      employeeStateCounts,
      employeeStateCountsLoading,
      canAddEmployees,
      canAddSingleEmployee,
      canIntegrate,
      isEnableEmployeeForm,
      isEnableShowBankingFile,
      postAnalyticsEvent,
      isUkCompany,
      isDisableSelfServiceReinviteEmail,
    };
  },
  mounted() {
    this.fetchEmployeesToReinvite();
  },
  methods: {
    async fetchEmployeesToReinvite() {
      if (!this.isUkCompany || this.isDisableSelfServiceReinviteEmail) {
        return [];
      }
      try {
        const data = await ApiClient.getEmployeesToReinvite(this.state.company.company_id);
        this.employeesToReinviteCount = data.employees.length;
      } catch (error) {
        console.error('Error fetching employees to reinvite:', error);
        // Optionally, handle the error, e.g., show a notification to the user
        this.employeesToReinviteCount = 0; // Ensure it's 0 in case of an error
      }
    },
    downloadBankingReport() {
      let fileName = 'banking.csv';
      const browser_integration = this.integrationsState.state.integrations.find(
        integration => integration['partner'] == 'BROWSER_UPLOAD' && 'daily_sftp_report' in integration['properties'],
      );
      if (browser_integration) {
        fileName = browser_integration['properties']['daily_sftp_report'];
      }
      ApiClient.downloadBankingReport(this.state.company.company_id, fileName);
    },
    goToAddEmployee() {
      this.postAnalyticsEvent({
        intent: 'navigate_employees_add_employee_single',
        task: 'add_employees',
        action: 'click',
      });
      this.$router.push({ name: 'employees-add' });
    },
    goToBulkUpload() {
      this.postAnalyticsEvent({
        intent: 'navigate_employees_add_employee_bulk',
        task: 'add_employees',
        action: 'click',
      });
      this.$router.push({ name: 'employees-upload' });
    },
    goToReinvitePage() {
      this.postAnalyticsEvent({
        intent: 'navigate_employees_reinvite',
        task: 'reinvite_employees',
        action: 'click',
      });
      this.$router.push({ name: 'employees-reinvite' });
    },
    handleMetricFilterChange(filterKey) {
      this.postAnalyticsEvent({
        intent: 'employees_filter_change',
        task: 'filter_employees',
        action: 'click',
        filter: filterKey,
      });

      if (this.$refs.employeesList) {
        const statusMapping = {
          ELIGIBLE: ['NOT ENROLLED', 'ENROLLING', 'ENROLLED'],
          ENROLLED: ['ENROLLED'],
          ENROLLING: ['ENROLLING'],
        };

        if (statusMapping[filterKey]) {
          this.$refs.employeesList.applyMetricFilter(statusMapping[filterKey]);
        }
      }
    },
    handleMetricFilterChanged(filterKey) {
      this.activeMetricFilter = filterKey;
    },
  },
};
</script>

<style scoped lang="scss">
.reinvite-title {
  color: var(--palette-color-brand-primary);
}
</style>
